// =============================================
// PANEL DE GESTIÓN DE BACKUPS
// =============================================

import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Backup as BackupIcon,
  CloudDownload as DownloadIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import backupService from '../../services/backupService';

const BackupManagement = () => {
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);

  // Cargar estado al montar
  useEffect(() => {
    loadStatus();
  }, []);

  // Cargar estado y lista de backups
  const loadStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await backupService.getStatus();
      
      if (response.success) {
        setStatus(response.scheduler);
        setBackups(response.backups.backups || []);
      }
    } catch (err) {
      setError('Error al cargar estado de backups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Ejecutar backup completo
  const handleFullBackup = async () => {
    if (!window.confirm('¿Ejecutar backup completo del sistema?')) {
      return;
    }

    try {
      setExecuting(true);
      setError('');
      setSuccess('');
      
      const response = await backupService.executeFullBackup();
      
      if (response.success) {
        setSuccess(`✅ Backup completado en ${response.results.duration}s`);
        await loadStatus(); // Recargar lista
      } else {
        setError('Error al ejecutar backup');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al ejecutar backup');
    } finally {
      setExecuting(false);
    }
  };

  // Ejecutar backup de base de datos
  const handleDatabaseBackup = async () => {
    if (!window.confirm('¿Ejecutar backup solo de base de datos?')) {
      return;
    }

    try {
      setExecuting(true);
      setError('');
      setSuccess('');
      
      const response = await backupService.executeDatabaseBackup();
      
      if (response.success) {
        setSuccess('✅ Backup de base de datos completado');
        await loadStatus();
      } else {
        setError('Error al ejecutar backup de BD');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al ejecutar backup');
    } finally {
      setExecuting(false);
    }
  };

  // Ejecutar backup de archivos
  const handleFilesBackup = async () => {
    if (!window.confirm('¿Ejecutar backup solo de archivos del sistema?')) {
      return;
    }

    try {
      setExecuting(true);
      setError('');
      setSuccess('');
      
      const response = await backupService.executeFilesBackup();
      
      if (response.success) {
        setSuccess('✅ Backup de archivos completado');
        await loadStatus();
      } else {
        setError('Error al ejecutar backup de archivos');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al ejecutar backup');
    } finally {
      setExecuting(false);
    }
  };

  // Descargar backup
  const handleDownload = async (filename) => {
    try {
      setError('');
      await backupService.downloadBackup(filename);
      setSuccess(`📥 Descargando: ${filename}`);
    } catch (err) {
      setError('Error al descargar backup');
      console.error(err);
    }
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-PA', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackupIcon sx={{ fontSize: 40, color: '#1976d2', mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">
            Gestión de Backups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Respaldo automático y manual del sistema
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadStatus}
          disabled={loading}
        >
          Actualizar
        </Button>
      </Box>

      {/* Error / Success */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Estado del Scheduler */}
      {status && (
        <Alert 
          severity={status.active ? 'info' : 'warning'} 
          sx={{ mb: 3 }}
          icon={<ScheduleIcon />}
        >
          <Typography variant="body2" gutterBottom>
            <strong>Backup Automático:</strong> {status.active ? 'Activo' : 'Inactivo'}
          </Typography>
          <Typography variant="body2">
            📅 Programación: {status.schedule} (Zona horaria: {status.timezone})
          </Typography>
          <Typography variant="body2">
            ⏰ Próximo backup: {status.nextRun}
          </Typography>
        </Alert>
      )}

      {/* Acciones de Backup */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Backup Completo */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BackupIcon color="primary" sx={{ mr: 1, fontSize: 40 }} />
                <Typography variant="h6">
                  Backup Completo
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Respalda la base de datos completa y todos los archivos del sistema.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Incluye: Base de datos + Código fuente + Configuración
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                fullWidth
                onClick={handleFullBackup}
                disabled={executing || loading}
                startIcon={executing ? <CircularProgress size={20} /> : <BackupIcon />}
              >
                {executing ? 'Ejecutando...' : 'Ejecutar Backup'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Backup de Base de Datos */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon color="success" sx={{ mr: 1, fontSize: 40 }} />
                <Typography variant="h6">
                  Solo Base de Datos
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Respalda únicamente la base de datos SQL Server.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Incluye: Todas las tablas y datos
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="success"
                fullWidth
                onClick={handleDatabaseBackup}
                disabled={executing || loading}
                startIcon={<StorageIcon />}
              >
                Backup de BD
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Backup de Archivos */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FileIcon color="warning" sx={{ mr: 1, fontSize: 40 }} />
                <Typography variant="h6">
                  Solo Archivos
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Respalda el código fuente y archivos de configuración.
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                Incluye: Controladores, rutas, servicios, config
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="warning"
                fullWidth
                onClick={handleFilesBackup}
                disabled={executing || loading}
                startIcon={<FileIcon />}
              >
                Backup de Archivos
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de Backups */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            📁 Backups Disponibles
          </Typography>
          <Chip 
            label={`${backups.length} archivo${backups.length !== 1 ? 's' : ''}`}
            color="primary"
            size="small"
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : backups.length === 0 ? (
          <Alert severity="info">
            No hay backups disponibles. Ejecuta tu primer backup.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Archivo</strong></TableCell>
                  <TableCell align="center"><strong>Tipo</strong></TableCell>
                  <TableCell align="right"><strong>Tamaño</strong></TableCell>
                  <TableCell align="center"><strong>Fecha</strong></TableCell>
                  <TableCell align="center"><strong>Antigüedad</strong></TableCell>
                  <TableCell align="center"><strong>Acciones</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((backup, index) => (
                  <TableRow key={index} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {backup.fileName.includes('DB_Backup') ? (
                          <StorageIcon sx={{ mr: 1, color: '#4caf50' }} />
                        ) : (
                          <FileIcon sx={{ mr: 1, color: '#ff9800' }} />
                        )}
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                          {backup.fileName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={backup.fileName.includes('DB') ? 'Base de Datos' : 'Archivos'}
                        size="small"
                        color={backup.fileName.includes('DB') ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {backup.size}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatDate(backup.date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={backup.age}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleDownload(backup.fileName)}
                        title="Descargar backup"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {backups.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" icon={<ScheduleIcon />}>
              <Typography variant="body2">
                Los backups se mantienen por <strong>7 días</strong> automáticamente. 
                Los archivos más antiguos se eliminan para ahorrar espacio.
              </Typography>
            </Alert>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default BackupManagement;
